import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@omni/services/alert/alert.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import {
  MedicalInquiryDualSignatureModalComponent,
  MedicalInquiryDualSignatureResult,
} from '@omni/components/medical-inquiry/medical-inquiry-dual-signature-modal/medical-inquiry-dual-signature-modal.component';

export interface RequestSubmitWithSignaturesOptions {
  hcpContactName: string;
}

/**
 * Runs the shared submit flow: confirmation alert then dual-signature modal.
 * Caller is responsible for building the submit payload and saving.
 */
@Injectable({
  providedIn: 'root',
})
export class MedicalInquirySubmitFlowService {
  constructor(
    private readonly alertService: AlertService,
    private readonly modalCtrl: ModalController,
    private readonly translate: TranslateService,
    private readonly authenticationService: AuthenticationService
  ) {}

  /**
   * Shows submit confirmation alert, then dual-signature modal (unless both iO configs are off).
   * When both BU_MEDICALINQUIRY_GA_MANDATORYHCPSIGNATURE and BU_MEDICALINQUIRY_GA_USERSIGNATURE
   * are disabled, skips the signature modal and returns empty signatures so the caller can submit directly.
   * @returns Signature result if user confirmed; null if cancelled at any step.
   */
  async requestSubmitWithSignatures(
    options: RequestSubmitWithSignaturesOptions
  ): Promise<MedicalInquiryDualSignatureResult | null> {
    const alertResult = await this.alertService.showAlert(
      {
        title: this.translate.instant('SUBMIT_MEDICAL_INQUIRY'),
        message: this.translate.instant('SUBMIT_MEDICAL_INQUIRY_CONFIRMATION'),
      },
      this.translate.instant('SUBMIT'),
      this.translate.instant('CANCEL')
    );
    if (alertResult?.role !== 'ok') return null;

    if (!this.showHcpSignature && !this.showRepSignature) {
      return { hcpSignature: '', repSignature: '' };
    }

    const modal = await this.modalCtrl.create({
      component: MedicalInquiryDualSignatureModalComponent,
      componentProps: {
        hcpContactName: options.hcpContactName,
        showHcpSignature: this.showHcpSignature,
        showRepSignature: this.showRepSignature,
        hcpSignatureTerms: this.hcpSignatureTerms,
      },
      backdropDismiss: false,
      cssClass: 'medical-inquiry-dual-signature-modal',
    });
    await modal.present();
    const signatureResult = await modal.onDidDismiss();
    return signatureResult?.data ?? null;
  }

  private get showHcpSignature(): boolean {
    return Boolean(
      this.authenticationService.hasIoConfigWithFeatureCode(
        IoConfigurationFeatureCodes.BU_MEDICALINQUIRY_GA_MANDATORYHCPSIGNATURE
      )
    );
  }

  private get showRepSignature(): boolean {
    return Boolean(
      this.authenticationService.hasIoConfigWithFeatureCode(
        IoConfigurationFeatureCodes.BU_MEDICALINQUIRY_GA_USERSIGNATURE
      )
    );
  }

  private get hcpSignatureTerms(): string {
    return (this.authenticationService.hasIoConfigWithFeatureCode(
      IoConfigurationFeatureCodes.BU_MEDICALINQUIRY_GA_ENABLEHCPSIGNATURETERMS
    ) as string) || '';
  }
}
